import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { useGetOrder } from "./useGetOrder"
import { OrderEntity } from "../../Objects/Order/OrderEntity"
import { PostFormRequest, PostRequest, PutFormRequest } from "../../services/ApiClientService"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { converteDateToSql } from "../../Objects/Generics/Helps"
import { useTranslation } from "react-i18next"
import { EOrderStatusType } from "../../Enum/EOrderStatus"
import { ERequestType } from "../../Enum/ERequestType"
import { useUpgradePlan } from "../../context/useUpgradePlanContext"
import { IShippingFeeResultDto } from "../../Interfaces/IShippingFeeResultDto"
import { companyMemory } from "../../Objects/Memory/Memory"
import { getRestaurantNow } from "../../utils/timeZone"

export const useSaveOrder = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const { handleGetOrder } = useGetOrder()

  const { openPlan } = useUpgradePlan()

  const isPlanLimitReachedMessage = (message: string) => {
    const normalizedMessage = message.toLowerCase()

    return (
      normalizedMessage.includes("limite mensal") ||
      normalizedMessage.includes("limite de pedidos") ||
      normalizedMessage.includes("Plan")
    )
  }

  const handleSaveOrder = async (order: OrderEntity, onSuccess: any, isCancel?: boolean, isToPay?: boolean) => {
    try {
      enableLoad()

      var response = null

      if (isToPay) {
        response = await PostFormRequest(`${API_ROUTES.ORDER.PAY}/${order.id}`, order.toJSON())
      } else if (isCancel) {
        response = await PostFormRequest(`${API_ROUTES.ORDER.CANCEL}/${order.id}`, order.orderCanceledEntity!.toJSON())
      } else {
        if (order.id > 0) {
          response = await PutFormRequest(`${API_ROUTES.ORDER.PUT}/${order.id}`, order.toJSON())
        } else {
          response = await PostFormRequest(API_ROUTES.ORDER.CREATE, order.toJSON())
        }
      }

      if (response.success) {
        await handleGetOrder(null, converteDateToSql(getRestaurantNow(companyMemory.timeZoneId)))

        await onSuccess?.()

        return response.data ? new OrderEntity(response.data) : undefined
      }

      throw new Error(response.message)
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")

      if (message.toLowerCase().includes("Plan")) {
        openPlan()
      }

      setError(t("label_title_erro"), message)

      return undefined
    } finally {
      disableLoad()
    }
  }

  const handleSavePublicOrder = async (
    order: OrderEntity,
    rote: string,
    onSuccess: any,
    onError?: (message: string) => void,
    requestType: ERequestType = ERequestType.Customer,
  ) => {
    try {
      enableLoad()

      var response = null

      response = await PostFormRequest(rote, order.toJSON(), requestType)

      if (response.success) {
        onSuccess && (await onSuccess(response.data))

        return true
      }

      throw new Error(response.message)
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")

      if (isPlanLimitReachedMessage(message) && onError) {
        onError(message)

        return false
      }

      setError(t("label_title_erro"), message)

      onError?.(message)

      return false
    } finally {
      disableLoad()
    }
  }

  const handleSaveOrderStatus = async (id: number, orderStatusType: EOrderStatusType, onSuccess: any) => {
    try {
      enableLoad()

      var response = await PostRequest(`${API_ROUTES.ORDER.UPDATE_DELIVERY_STATUS}/${id}`, {
        status: orderStatusType,
      })

      if (response.success) {
        onSuccess && onSuccess(response.data)

        return
      }

      throw new Error(response.message)
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleCalculateShippingFee = async (targetAddressId: number, orderSubtotal: number, onSuccess: any) => {
    try {
      enableLoad()

      const response = await PostRequest<IShippingFeeResultDto>(
        API_ROUTES.DELIVERY.CALCULATE_SHIPPING_FELL,
        {
          AddressEntityId: targetAddressId,
          OrderSubtotal: orderSubtotal,
        },
        ERequestType.Customer,
      )

      if (response.success) {
        onSuccess && onSuccess(response.data)

        return
      }

      throw new Error(response.message)
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleSaveItemOrderStatus = async (id: number, genericFoodId: number, isNextStep: boolean, onSuccess: any) => {
    try {
      enableLoad()

      var response = await PostRequest(`${API_ROUTES.ORDER.UPDATE_DELIVERY_ITEM_STATUS}/${id}`, {
        OrderGenericFoodId: genericFoodId,
        IsNextStep: isNextStep,
      })

      if (response.success) {
        onSuccess && onSuccess(response.data)

        return
      }

      throw new Error(response.message)
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  return {
    handleSaveOrder,
    handleSaveOrderStatus,
    handleSaveItemOrderStatus,
    handleSavePublicOrder,
    handleCalculateShippingFee,
  }
}
